import { useMemo, useRef, useState } from 'react'
import { Avatar, Button, Input, Popconfirm, Typography, Divider, Image, Segmented, Tooltip, theme } from 'antd'
import {
    CloseOutlined,
    TeamOutlined,
    LinkOutlined,
    FileOutlined,
    PictureOutlined,
    BellOutlined,
    EditOutlined,
    CameraOutlined,
    UserAddOutlined,
    LogoutOutlined,
    DeleteOutlined,
    CrownFilled,
} from '@ant-design/icons'
import type { ChangeEvent } from 'react'
import type { ChatNotificationLevel } from '../../Contracts'
import { observer } from 'mobx-react-lite'
import { HEIC_ACCEPT, preparePhoto } from '@/Ship/Helpers/heicPhoto'
import { notify } from '@/Ship/Helpers/notify'
import { useStore } from '../../Stores'
import { formatLastSeen } from '../../Helpers/formatDate'
import { formatFileSize } from '../../Helpers/fileIcon'
import { CHAT_PRESENCE_ENABLED } from '../../Helpers/chatFeatures'
import OnlineIndicator from '../../Components/common/OnlineIndicator'
import AddParticipantsModal from './AddParticipantsModal'

const { Text, Title } = Typography

const PANEL_WIDTH = 320
const MEDIA_GRID_SIZE = 90
const MEDIA_PREVIEW_LIMIT = 9
const FILES_PREVIEW_LIMIT = 5
const LINKS_PREVIEW_LIMIT = 10

const ChatInfoPanel = observer(() => {
    const { uiStore, chatStore, userStore, messageStore } = useStore()
    const { token } = theme.useToken()
    const [isEditingName, setIsEditingName] = useState(false)
    const [nameDraft, setNameDraft] = useState('')
    const [isAddOpen, setIsAddOpen] = useState(false)
    const avatarInputRef = useRef<HTMLInputElement>(null)

    const chat = chatStore.activeChat
    if (!chat) return null

    const displayName = chatStore.getChatDisplayName(chat)
    const avatarUrl = chatStore.getChatAvatar(chat)
    // Права дзеркалять ChatRule: власник керує, учасник може вийти
    const isOwner = chatStore.isOwner(chat)
    const isGroup = chat.type === 'group'
    const canManage = isOwner && isGroup
    const canLeave = !isOwner && chat.participantIds.includes(userStore.currentUserId)
    const canDelete = isOwner && !chat.entityType

    const startNameEdit = () => {
        setNameDraft(chat.name ?? '')
        setIsEditingName(true)
    }

    const submitName = () => {
        setIsEditingName(false)
        if (nameDraft.trim() && nameDraft.trim() !== chat.name) {
            void chatStore.renameChat(chat.id, nameDraft)
        }
    }

    const handleAvatarSelected = async (e: ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0]
        if (!selected) return
        // HEIC з iPhone → JPEG (сервер зберіг би HEIC, і Chrome його не показав би) + стиснення
        try {
            void chatStore.uploadChatAvatar(chat.id, await preparePhoto(selected))
        } catch (error) {
            notify.error('Не вдалося змінити аватар', { description: error instanceof Error ? error.message : '' })
        }
    }

    // Get other user for private chats
    const otherId =
        chat.type === 'private' ? chat.participantIds.find((id: string) => id !== userStore.currentUserId) : undefined
    const otherUser = otherId ? userStore.getById(otherId) : undefined
    const isOnline = otherUser?.status === 'online'

    // Collect media from chat messages (memoized — heavy filtering)
    const chatMessages = messageStore.messages.filter((m) => m.chatId === chat.id && !m.isDeleted)

    const photos = useMemo(
        () =>
            chatMessages
                .filter((m) => m.files?.some((f) => f.type === 'photo'))
                .flatMap((m) => m.files!.filter((f) => f.type === 'photo').map((f) => ({ msgId: m.id, file: f }))),
        [chatMessages]
    )

    const files = useMemo(
        () =>
            chatMessages
                .filter((m) => m.files?.some((f) => f.type === 'file'))
                .flatMap((m) => m.files!.filter((f) => f.type === 'file').map((f) => ({ msgId: m.id, file: f }))),
        [chatMessages]
    )

    const links = useMemo(() => {
        const urlRegex = /https?:\/\/[^\s]+/g
        const result: { url: string; messageId: string }[] = []
        for (const m of chatMessages) {
            if (!m.content) continue
            const matches = m.content.match(urlRegex)
            if (matches) {
                for (const url of matches) result.push({ url, messageId: m.id })
            }
        }
        return result
    }, [chatMessages])

    const sectionStyle = {
        padding: '12px 16px',
    }

    const sectionTitleStyle = {
        display: 'flex' as const,
        alignItems: 'center' as const,
        gap: 8,
        marginBottom: 8,
        fontSize: 13,
        fontWeight: 600 as const,
        color: token.colorTextSecondary,
    }

    return (
        <div
            style={{
                width: PANEL_WIDTH,
                minWidth: PANEL_WIDTH,
                borderLeft: `1px solid ${token.colorBorderSecondary}`,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background: token.colorBgContainer,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    gap: 8,
                }}
            >
                <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined style={{ fontSize: 14 }} />}
                    onClick={() => uiStore.closeChatInfo()}
                    style={{ color: token.colorTextSecondary }}
                />
                <Text strong style={{ fontSize: 15 }}>
                    Про чат
                </Text>
            </div>

            {/* Scrollable content */}
            <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
                {/* Avatar + Name block */}
                <div style={{ textAlign: 'center', padding: '24px 16px 16px' }}>
                    <div
                        {...(canManage
                            ? { className: 'chat-avatar-editable', onClick: () => avatarInputRef.current?.click() }
                            : {})}
                        style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}
                    >
                        <Avatar
                            src={chat.avatarUrl ?? avatarUrl}
                            size={96}
                            icon={chat.type === 'group' ? <TeamOutlined /> : undefined}
                            {...(canManage ? { style: { cursor: 'pointer' } } : {})}
                        >
                            {!(chat.avatarUrl ?? avatarUrl) && displayName.charAt(0)}
                        </Avatar>
                        {canManage && (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: 0,
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    background: token.colorPrimary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}
                            >
                                <CameraOutlined style={{ color: '#fff', fontSize: 14 }} />
                            </div>
                        )}
                    </div>
                    {canManage && (
                        <input
                            ref={avatarInputRef}
                            type="file"
                            accept={`image/*,${HEIC_ACCEPT}`}
                            style={{ display: 'none' }}
                            onChange={(e) => void handleAvatarSelected(e)}
                        />
                    )}
                    {isEditingName ? (
                        <Input
                            size="small"
                            autoFocus
                            value={nameDraft}
                            maxLength={255}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onPressEnter={submitName}
                            onBlur={submitName}
                            style={{ maxWidth: 240, textAlign: 'center' }}
                        />
                    ) : (
                        <Title level={5} style={{ margin: 0 }}>
                            {displayName}
                            {canManage && (
                                <Tooltip title="Перейменувати">
                                    <EditOutlined
                                        onClick={startNameEdit}
                                        style={{
                                            fontSize: 14,
                                            marginLeft: 6,
                                            color: token.colorTextSecondary,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Title>
                    )}
                    {chat.type === 'private' && otherUser && CHAT_PRESENCE_ENABLED && (
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 13,
                                color: isOnline ? '#52c41a' : undefined,
                            }}
                        >
                            {isOnline ? 'онлайн' : formatLastSeen(otherUser.lastSeen)}
                        </Text>
                    )}
                    {chat.type === 'group' && (
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {chat.participantIds.length} учасників
                        </Text>
                    )}
                </div>

                <Divider style={{ margin: 0 }} />

                {/* Notifications: personal per-chat level (participants only) */}
                {chat.participantIds.includes(userStore.currentUserId) && (
                    <>
                        <div style={sectionStyle}>
                            <div style={sectionTitleStyle}>
                                <BellOutlined />
                                Сповіщення
                            </div>
                            <Segmented
                                block
                                size="small"
                                value={chat.notificationLevel}
                                onChange={(value) =>
                                    chatStore.setNotificationLevel(chat.id, value as ChatNotificationLevel)
                                }
                                options={[
                                    { label: 'Всі', value: 'all' },
                                    { label: 'Без тостів', value: 'badge' },
                                    { label: 'Вимкнено', value: 'muted' },
                                ]}
                            />
                        </div>

                        <Divider style={{ margin: 0 }} />
                    </>
                )}

                {/* Group members */}
                {chat.type === 'group' && (
                    <>
                        <div style={sectionStyle}>
                            <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TeamOutlined />
                                    Учасники ({chat.participantIds.length})
                                </span>
                                {canManage && (
                                    <Tooltip title="Додати учасників">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<UserAddOutlined />}
                                            onClick={() => setIsAddOpen(true)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            {chat.participantIds.map((pid: string) => {
                                const user = userStore.getById(pid)
                                if (!user) return null
                                const isChatOwner = pid === chat.ownerId
                                return (
                                    <div
                                        key={pid}
                                        className="chat-panel-row"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '4px 6px',
                                        }}
                                    >
                                        <div style={{ position: 'relative' }}>
                                            <Avatar src={user.avatarUrl} size={32}>
                                                {user.firstName.charAt(0)}
                                            </Avatar>
                                            {user.status === 'online' && <OnlineIndicator size={8} borderWidth={1.5} />}
                                        </div>
                                        <Text style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
                                            {user.firstName} {user.lastName}
                                            {isChatOwner && (
                                                <Tooltip title="Власник чату">
                                                    <CrownFilled
                                                        style={{ color: '#faad14', marginLeft: 6, fontSize: 13 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Text>
                                        {/* Видаляти може власник, будь-кого крім себе (дзеркало бек-правила) */}
                                        {canManage && !isChatOwner && (
                                            <Popconfirm
                                                title={`Видалити ${user.firstName} з чату?`}
                                                okText="Видалити"
                                                cancelText="Скасувати"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => void chatStore.removeParticipant(chat.id, pid)}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    danger
                                                    icon={<CloseOutlined style={{ fontSize: 11 }} />}
                                                />
                                            </Popconfirm>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                        <Divider style={{ margin: 0 }} />
                    </>
                )}

                {/* Photos / Media */}
                {photos.length > 0 && (
                    <>
                        <div style={sectionStyle}>
                            <div style={sectionTitleStyle}>
                                <PictureOutlined />
                                Медіа ({photos.length})
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                <Image.PreviewGroup>
                                    {photos.slice(0, MEDIA_PREVIEW_LIMIT).map((item, i) => (
                                        <Image
                                            key={`${item.msgId}-${i}`}
                                            src={item.file.url}
                                            width={MEDIA_GRID_SIZE}
                                            height={MEDIA_GRID_SIZE}
                                            style={{ objectFit: 'cover', borderRadius: 4 }}
                                        />
                                    ))}
                                </Image.PreviewGroup>
                                {photos.length > MEDIA_PREVIEW_LIMIT && (
                                    <div
                                        style={{
                                            width: MEDIA_GRID_SIZE,
                                            height: MEDIA_GRID_SIZE,
                                            borderRadius: 4,
                                            background: token.colorFillTertiary,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: token.colorTextSecondary,
                                            fontSize: 13,
                                        }}
                                    >
                                        +{photos.length - MEDIA_PREVIEW_LIMIT}
                                    </div>
                                )}
                            </div>
                        </div>
                        <Divider style={{ margin: 0 }} />
                    </>
                )}

                {/* Files */}
                {files.length > 0 && (
                    <>
                        <div style={sectionStyle}>
                            <div style={sectionTitleStyle}>
                                <FileOutlined />
                                Файли ({files.length})
                            </div>
                            {files.slice(0, FILES_PREVIEW_LIMIT).map((item, i) => (
                                <div
                                    key={`${item.msgId}-${i}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '4px 0',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FileOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text
                                            style={{
                                                fontSize: 13,
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.file.name}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {formatFileSize(item.file.size)}
                                        </Text>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Divider style={{ margin: 0 }} />
                    </>
                )}

                {/* Links */}
                {links.length > 0 && (
                    <div style={sectionStyle}>
                        <div style={sectionTitleStyle}>
                            <LinkOutlined />
                            Посилання ({links.length})
                        </div>
                        {links.slice(0, LINKS_PREVIEW_LIMIT).map((link, i) => (
                            <div key={`${link.messageId}-${i}`} style={{ padding: '3px 0' }}>
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: token.colorPrimary,
                                        wordBreak: 'break-all',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {link.url}
                                </Text>
                            </div>
                        ))}
                    </div>
                )}

                {/* Небезпечна зона: вихід (учасник) / видалення (власник, не entity-чат) */}
                {isGroup && (canLeave || canDelete) && (
                    <>
                        <Divider style={{ margin: 0 }} />
                        <div style={sectionStyle}>
                            {canLeave && (
                                <Popconfirm
                                    title="Вийти з чату? Історія стане недоступною."
                                    okText="Вийти"
                                    cancelText="Скасувати"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => void chatStore.leaveChat(chat.id)}
                                >
                                    <Button block danger type="text" icon={<LogoutOutlined />}>
                                        Вийти з чату
                                    </Button>
                                </Popconfirm>
                            )}
                            {canDelete && (
                                <Popconfirm
                                    title="Видалити чат для всіх учасників?"
                                    okText="Видалити"
                                    cancelText="Скасувати"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => void chatStore.deleteChat(chat.id)}
                                >
                                    <Button block danger type="text" icon={<DeleteOutlined />}>
                                        Видалити чат
                                    </Button>
                                </Popconfirm>
                            )}
                        </div>
                    </>
                )}
            </div>

            <AddParticipantsModal chatId={chat.id} open={isAddOpen} onClose={() => setIsAddOpen(false)} />
        </div>
    )
})

export default ChatInfoPanel
